import React, { useMemo, useRef, useState } from "react";
import { Plus, Undo2, RotateCcw, Download, Sparkles, X } from "lucide-react";
import {
  MANDALA_PALETTE,
  MANDALA_BACKGROUNDS,
  MANDALA_MOTIFS,
  SEGMENT_OPTIONS,
} from "../../data/mandala";
import ShareArtwork from "./ShareArtwork";

const CANVAS = 600;
const CENTER = CANVAS / 2;
const START_R = 32;      // first ring radius from centre
const MAX_R = 270;       // outer limit
const DEFAULT_SPACING = 30;

export default function MandalaActivity({ onSave }) {
  const svgRef = useRef(null);

  // Builder defaults
  const [motifId, setMotifId] = useState("petal");
  const [color, setColor] = useState(MANDALA_PALETTE[0].value); // gold
  const [thickness, setThickness] = useState(6);
  const [segments, setSegments] = useState(12);
  const [spacing, setSpacing] = useState(DEFAULT_SPACING);
  const [bg, setBg] = useState(MANDALA_BACKGROUNDS[0].value);   // midnight
  const [bindu, setBindu] = useState(true);
  const [rings, setRings] = useState([]); // [{ id, motifId, color, segments, thickness, r }]

  const nextR = useMemo(() => {
    if (rings.length === 0) return START_R;
    return rings[rings.length - 1].r + spacing;
  }, [rings, spacing]);

  const canAdd = nextR <= MAX_R;
  const isLight = bg === "#F9F6F0";
  const guideColor = isLight ? "#1E2A4F" : "#F9F6F0";

  const addRing = () => {
    if (!canAdd) return;
    const ring = {
      id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      motifId,
      color,
      segments,
      thickness,
      r: nextR,
    };
    setRings((p) => [...p, ring]);
  };

  const undoRing = () => setRings((p) => p.slice(0, -1));
  const clearAll = () => setRings([]);

  const removeAt = (idx) => {
    // Removing a middle ring would leave holes — we collapse outer rings inward
    // to keep the mandala continuous. Feels right for this kind of builder.
    setRings((p) => {
      const next = p.slice();
      next.splice(idx, 1);
      // Recompute radii so it stays evenly spaced and centred.
      return next.map((ring, i) => ({
        ...ring,
        r: START_R + i * spacing,
      }));
    });
  };

  const renderMotif = (motif, ring) => {
    if (motif.continuous) {
      return motif.render({ r: ring.r, color: ring.color, thickness: ring.thickness });
    }
    return Array.from({ length: ring.segments }).map((_, i) => {
      const rot = (360 * i) / ring.segments;
      return (
        <g key={`m-${ring.id}-${i}`} transform={`rotate(${rot})`}>
          {motif.render({ r: ring.r, color: ring.color, thickness: ring.thickness })}
        </g>
      );
    });
  };

  // Ghost preview of next ring
  const previewMotif = MANDALA_MOTIFS.find((m) => m.id === motifId);

  const downloadArt = async () => {
    const svg = svgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const out = document.createElement("canvas");
      out.width = 1200;
      out.height = 1200;
      const ctx = out.getContext("2d");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, 1200, 1200);
      ctx.drawImage(img, 0, 0, 1200, 1200);
      const link = document.createElement("a");
      link.download = "my-mandala.png";
      link.href = out.toDataURL("image/png");
      link.click();
      if (onSave) onSave();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 items-start" data-testid="mandala-activity">
      {/* Canvas */}
      <div className="lg:col-span-8">
        <div className="relative w-full aspect-square rounded-2xl overflow-hidden border-4 border-[#E5DFD3] shadow-xl">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${CANVAS} ${CANVAS}`}
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 w-full h-full"
            data-testid="mandala-canvas"
          >
            <rect width="100%" height="100%" fill={bg} />

            {/* Soft concentric guides — barely visible */}
            <g opacity={isLight ? 0.08 : 0.12}>
              {[60, 120, 180, 240].map((r) => (
                <circle
                  key={`g-${r}`}
                  cx={CENTER}
                  cy={CENTER}
                  r={r}
                  fill="none"
                  stroke={guideColor}
                  strokeWidth="1"
                  strokeDasharray="2 6"
                />
              ))}
            </g>

            {/* Mandala content — centred */}
            <g transform={`translate(${CENTER},${CENTER})`}>
              {bindu && (
                <circle cx={0} cy={0} r={6} fill={MANDALA_PALETTE[0].value} />
              )}

              {rings.map((ring) => {
                const motif = MANDALA_MOTIFS.find((m) => m.id === ring.motifId);
                if (!motif) return null;
                return (
                  <g
                    key={ring.id}
                    style={{ pointerEvents: "none" }}
                    data-testid={`mandala-ring-${ring.motifId}`}
                  >
                    {renderMotif(motif, ring)}
                  </g>
                );
              })}

              {/* Ghost preview of the next ring */}
              {canAdd && previewMotif && (
                <g opacity="0.32" data-testid="mandala-preview">
                  {renderMotif(previewMotif, {
                    id: "preview",
                    motifId,
                    color,
                    segments,
                    thickness,
                    r: nextR,
                  })}
                </g>
              )}
            </g>
          </svg>
        </div>
        <p className="mt-4 text-sm text-[#5C6A8F] text-center">
          Build your mandala <em className="italic">ring by ring</em> — pick a motif,
          colour and segments, then tap <strong>Add ring</strong> to grow it outwards.
        </p>

        {/* Ring stack */}
        {rings.length > 0 && (
          <div className="mt-5" data-testid="mandala-ring-stack">
            <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-2">
              Your rings — centre outwards
            </p>
            <div className="flex flex-wrap gap-2">
              {rings.map((ring, idx) => {
                const motif = MANDALA_MOTIFS.find((m) => m.id === ring.motifId);
                return (
                  <span
                    key={ring.id}
                    className="inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-full border border-[#E5DFD3] bg-white text-xs"
                  >
                    <span
                      className="w-3 h-3 rounded-full inline-block"
                      style={{ background: ring.color }}
                    />
                    <span className="text-[#1E2A4F]">
                      {idx + 1}. {motif ? motif.name : ring.motifId}
                      {!motif?.continuous && ` × ${ring.segments}`}
                    </span>
                    <button
                      onClick={() => removeAt(idx)}
                      data-testid={`mandala-ring-remove-${idx}`}
                      aria-label={`Remove ring ${idx + 1}`}
                      className="w-5 h-5 rounded-full hover:bg-[#E5DFD3] flex items-center justify-center text-[#5C6A8F]"
                    >
                      <X size={11} />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Tools */}
      <div className="lg:col-span-4 space-y-5">
        {/* Motif picker */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">
            Motif for next ring
          </p>
          <div className="grid grid-cols-5 gap-2">
            {MANDALA_MOTIFS.map((m) => (
              <button
                key={m.id}
                data-testid={`mandala-motif-${m.id}`}
                onClick={() => setMotifId(m.id)}
                aria-label={m.name}
                title={m.name}
                className={`aspect-square rounded-lg border-2 flex items-center justify-center transition-all ${
                  motifId === m.id
                    ? "border-[#6A4FB6] bg-[#EFEAFA]"
                    : "border-[#E5DFD3] bg-white hover:border-[#6A4FB6]"
                }`}
              >
                <svg viewBox="-30 -30 60 60" className="w-full h-full" color={color}>
                  <g color={color}>
                    {m.continuous
                      ? m.render({ r: 18, color, thickness: 3 })
                      : Array.from({ length: 8 }).map((_, i) => (
                          <g key={i} transform={`rotate(${(360 * i) / 8})`}>
                            {m.render({ r: 18, color, thickness: 3 })}
                          </g>
                        ))}
                  </g>
                </svg>
              </button>
            ))}
          </div>
        </div>

        {/* Colour */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Colour</p>
          <div className="grid grid-cols-6 gap-1.5">
            {MANDALA_PALETTE.map((c) => (
              <button
                key={c.value}
                data-testid={`mandala-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setColor(c.value)}
                aria-label={c.name}
                style={{ background: c.value }}
                className={`aspect-square rounded-md border-2 transition-transform ${
                  color === c.value
                    ? "scale-110 border-[#1E2A4F] ring-2 ring-[#1E2A4F]/30"
                    : "border-[#E5DFD3] hover:scale-105"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Segments */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">
            Segments
          </p>
          <div className="grid grid-cols-6 gap-1.5">
            {SEGMENT_OPTIONS.map((s) => (
              <button
                key={s}
                data-testid={`mandala-segments-${s}`}
                onClick={() => setSegments(s)}
                className={`px-2 py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                  segments === s
                    ? "bg-[#6A4FB6] text-[#F9F6F0] border-[#6A4FB6]"
                    : "border-[#E5DFD3] text-[#5C6A8F] hover:border-[#6A4FB6]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Thickness */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-2">
            Motif thickness
          </p>
          <input
            type="range"
            min="2"
            max="14"
            step="1"
            value={thickness}
            onChange={(e) => setThickness(parseInt(e.target.value, 10))}
            data-testid="mandala-thickness"
            className="w-full accent-[#6A4FB6]"
          />
        </div>

        {/* Spacing */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-2">
            Ring spacing
          </p>
          <input
            type="range"
            min="16"
            max="56"
            step="2"
            value={spacing}
            onChange={(e) => setSpacing(parseInt(e.target.value, 10))}
            data-testid="mandala-spacing"
            className="w-full accent-[#6A4FB6]"
          />
        </div>

        {/* Background */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-2">
            Background
          </p>
          <div className="flex gap-2">
            {MANDALA_BACKGROUNDS.map((b) => (
              <button
                key={b.value}
                data-testid={`mandala-bg-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setBg(b.value)}
                aria-label={b.name}
                style={{ background: b.value }}
                className={`w-9 h-9 rounded-full border-2 transition-transform ${
                  bg === b.value
                    ? "scale-110 border-[#6A4FB6] ring-2 ring-[#6A4FB6]/30"
                    : "border-[#E5DFD3] hover:scale-105"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Bindu toggle */}
        <label className="flex items-center gap-2 text-xs text-[#5C6A8F] cursor-pointer">
          <input
            type="checkbox"
            checked={bindu}
            onChange={(e) => setBindu(e.target.checked)}
            data-testid="mandala-bindu"
            className="accent-[#6A4FB6]"
          />
          Show centre <em className="italic">bindu</em> (centre dot)
        </label>

        {/* Add ring */}
        <button
          data-testid="mandala-add-ring"
          onClick={addRing}
          disabled={!canAdd}
          className={`w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full text-sm font-medium tracking-wide transition-colors duration-300 ${
            canAdd
              ? "bg-[#6A4FB6] text-[#F9F6F0] hover:bg-[#1E2A4F]"
              : "bg-[#E5DFD3] text-[#5C6A8F] cursor-not-allowed"
          }`}
        >
          <Plus size={16} />
          {canAdd
            ? rings.length === 0
              ? "Add first ring"
              : "Add another ring"
            : "Mandala complete — no more room"}
        </button>

        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="mandala-undo"
            onClick={undoRing}
            disabled={rings.length === 0}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-xs disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#1E2A4F]"
          >
            <Undo2 size={12} /> Undo ring
          </button>
          <button
            data-testid="mandala-clear"
            onClick={clearAll}
            disabled={rings.length === 0}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-xs disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#1E2A4F]"
          >
            <RotateCcw size={12} /> Clear
          </button>
        </div>

        <button
          data-testid="mandala-download"
          onClick={downloadArt}
          disabled={rings.length === 0}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-[#1E2A4F] text-[#F9F6F0] hover:bg-[#6A4FB6] transition-colors duration-300 text-sm font-medium tracking-wide disabled:opacity-40"
        >
          <Download size={16} /> Save my mandala
        </button>
        <ShareArtwork
          artFormId="mandala"
          artName="Mandala"
          accent="#6A4FB6"
          testIdPrefix="mandala"
        />

        <p className="text-[11px] text-[#5C6A8F] text-center leading-relaxed flex items-center justify-center gap-1">
          <Sparkles size={11} /> Tip: change colour and motif before each ring for variety.
        </p>
      </div>
    </div>
  );
}
